// biome-ignore-all lint/a11y/noLabelWithoutControl: admin-only setup form; labels are visual captions for the adjacent control (matches NewUserModal).
// =============================================================================
// NewShelterWizard.jsx  --  stepped "New DFV Service" setup
// =============================================================================
// Replaces the single-page New DFV Service modal with a stepped wizard that walks
// the admin through the same tabs the editor has: Details -> Address -> Delivery.
// (A Users/Caseworker step is planned as a follow-up; it needs the account-creation
// helper extraction — see the DFV wizard task.)
//
// It reuses the shared `newForm`/`setNewForm`/`createUser` plumbing, so the create
// path is UNCHANGED: pressing "Create DFV Service" on the last step calls
// createUser("shelters"), which already inserts every shelter column + the default
// delivery address (useUserAdminHandlers). This component only restructures the
// capture UI into steps and adds the Address-tab fields the old modal was missing
// (postal line2/city/state + the full physical address).
// =============================================================================

import React from "react";
import { ADDRESS_NAME_PLACEHOLDER } from "../../services/addressName";
import {
  findCodeDuplicate,
  findNameDuplicate,
  missingRequiredShelterFields,
  nextShelterCode,
} from "../../services/shelterValidation";
import { Btn } from "../../utils/ui.jsx";
import { AddressAutocomplete } from "../form/AddressAutocomplete.jsx";
import { AddressNameHelp } from "../form/AddressNameHelp.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
const STEPS = ["Details", "Address", "Users", "Delivery"];
const CONTACT_ROLES = [
  "DFV Service Manager",
  "Caseworker",
  "Admin Officer",
  "Coordinator",
  "Other",
];

const labelCls = "text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1";
const inputCls =
  "w-full py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white";

// Red " *" appended to a mandatory field's label.
function ReqStar() {
  return <span className="text-zephyr-red">&nbsp;*</span>;
}

// A labelled text input bound to a newForm key. `req` shows the mandatory asterisk;
// `disabled` greys the field (e.g. Customer Code until a State is chosen).
function Field({ label, value, onChange, placeholder, full, req, type, disabled, hint }) {
  return (
    <div className={full ? "col-span-full" : ""}>
      <label className={labelCls}>
        {label}
        {req && <ReqStar />}
      </label>
      <input
        type={type || "text"}
        value={value || ""}
        disabled={disabled}
        onChange={function (e) {
          onChange(e.target.value);
        }}
        placeholder={placeholder}
        className={`${inputCls} disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted disabled:cursor-not-allowed`}
        style={{ fontFamily: "inherit" }}
      />
      {hint && <div className="text-[11px] text-zephyr-muted mt-[3px]">{hint}</div>}
    </div>
  );
}

// A labelled <select> with an options list ([{value,label}] or plain strings).
function SelectField({ label, value, onChange, options, placeholder, req }) {
  return (
    <div>
      <label className={labelCls}>
        {label}
        {req && <ReqStar />}
      </label>
      <select
        value={value || ""}
        onChange={function (e) {
          onChange(e.target.value);
        }}
        className={inputCls}
        style={{ fontFamily: "inherit" }}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map(function (o) {
          const val = typeof o === "string" ? o : o.value;
          const lab = typeof o === "string" ? o : o.label;
          return (
            <option key={val} value={val}>
              {lab}
            </option>
          );
        })}
      </select>
    </div>
  );
}

export function NewShelterWizard({
  open,
  onClose,
  newForm,
  setNewForm,
  newSaving,
  newErr,
  setNewErr,
  createUser,
  customerTypes,
  salesGroups,
  warehouses,
  shelters, // ALL services incl. deactivated — for code generation + duplicate checks
  mapsReadyTick,
}) {
  const [step, setStep] = React.useState(0);
  // The last code we auto-generated, so a later State change only overwrites the
  // code when the admin hasn't typed their own (John: code is editable).
  const autoCodeRef = React.useRef("");
  // Reset to the first step each time the wizard opens; default Date Onboarded to today.
  React.useEffect(
    function () {
      if (open) {
        setStep(0);
        setNewForm(function (f) {
          if (f.date_onboarded) return f;
          return Object.assign({}, f, { date_onboarded: new Date().toISOString().slice(0, 10) });
        });
      }
    },
    [open, setNewForm]
  );
  if (!open) return null;

  const set = function (patch) {
    setNewForm(function (f) {
      return Object.assign({}, f, patch);
    });
  };
  const setF = function (key) {
    return function (val) {
      set({ [key]: val });
    };
  };
  // Copy the Step-2 physical address into the Step-4 delivery address (mirrors the
  // ShelterDetail "Copy from physical address" convenience). Suburb/City maps from
  // physical_suburb_city → delivery_suburb.
  const copyPhysicalToDelivery = function () {
    set({
      delivery_line1: newForm.physical_line1 || "",
      delivery_line2: newForm.physical_line2 || "",
      delivery_suburb: newForm.physical_suburb_city || "",
      delivery_state: newForm.physical_state || "",
      delivery_postcode: newForm.physical_postcode || "",
    });
  };

  const isLast = step === STEPS.length - 1;
  // Step-3 first user: "No login account (contact only)" hides the password and
  // makes the email optional (createUser creates a login-less membership instead).
  const cwNoLogin = newForm.cw_no_login === true;

  // State drives the Customer Code (Zephyr<STATE><NNN>). Auto-fill the code unless
  // the admin has typed their own (code stays editable). Also sets state.state.
  function onStateChange(v) {
    setNewForm(function (f) {
      const patch = { state: v };
      if (!f.shelter_code || f.shelter_code === autoCodeRef.current) {
        const code = nextShelterCode(v, shelters);
        patch.shelter_code = code;
        autoCodeRef.current = code;
      }
      return Object.assign({}, f, patch);
    });
  }

  // Non-blocking duplicate-NAME warning (incl. deactivated services).
  const nameDup = findNameDuplicate(newForm.shelter_name, shelters);

  function next() {
    setNewErr("");
    if (step === 0) {
      const missing = missingRequiredShelterFields(newForm);
      if (!String(newForm.shelter_code || "").trim()) missing.unshift("Customer Code");
      if (missing.length) {
        setNewErr(`Please complete the required field(s): ${missing.join(", ")}.`);
        return;
      }
      // Code must be unique — checked against ACTIVE and DEACTIVATED services.
      const dup = findCodeDuplicate(newForm.shelter_code, shelters);
      if (dup) {
        setNewErr(
          dup.deactivated
            ? `Customer Code "${newForm.shelter_code}" belongs to a DEACTIVATED service ("${dup.shelter_name}"). Reactivate that service instead, or choose a different code.`
            : `Customer Code "${newForm.shelter_code}" is already used by the active service "${dup.shelter_name}". Choose a different code.`
        );
        return;
      }
    }
    setStep(function (s) {
      return Math.min(s + 1, STEPS.length - 1);
    });
  }
  function back() {
    setNewErr("");
    setStep(function (s) {
      return Math.max(s - 1, 0);
    });
  }
  function copyPhysicalToPostal() {
    set({
      postal_line1: newForm.physical_line1 || "",
      postal_line2: newForm.physical_line2 || "",
      postal_suburb: newForm.physical_suburb || "",
      postal_suburb_city: newForm.physical_suburb_city || "",
      postal_state: newForm.physical_state || "",
      postal_postcode: newForm.physical_postcode || "",
    });
  }

  return (
    <ModalBackdrop
      onClose={onClose}
      contentStyle={{
        background: "#FFFFFF",
        borderRadius: 14,
        width: "calc(100vw - 40px)",
        maxWidth: 640,
        boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
        padding: 24,
        maxHeight: "90vh",
        overflowY: "auto",
      }}
    >
      <div className="font-bold text-[16px] text-zephyr-teal-dark mb-1">New DFV Service</div>

      {/* Step progress */}
      <div className="flex items-center gap-2 mb-4">
        {STEPS.map(function (name, i) {
          return (
            <div key={name} className="flex items-center gap-2">
              <span
                className={`text-[12px] font-semibold px-2 py-[2px] rounded-full ${
                  i === step
                    ? "bg-zephyr-teal text-white"
                    : i < step
                      ? "bg-[#E8F5E9] text-[#2E7D32]"
                      : "bg-[#ECEFF1] text-zephyr-muted"
                }`}
              >
                {i + 1}. {name}
              </span>
              {i < STEPS.length - 1 ? <span className="text-zephyr-muted">→</span> : null}
            </div>
          );
        })}
      </div>

      {/* STEP 1 — Details */}
      {step === 0 && (
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="DFV Service Name"
            req
            value={newForm.shelter_name}
            onChange={setF("shelter_name")}
          />
          <Field
            label="Customer Code"
            req
            value={newForm.shelter_code}
            onChange={setF("shelter_code")}
            disabled={!newForm.state}
            placeholder={newForm.state ? "Auto-generated — edit if needed" : "Select a State first"}
            hint={
              newForm.state
                ? "Suggested from the State; you can change it."
                : "Choose a State to generate a code."
            }
          />
          {/* Name-duplicate warning (non-blocking): names the match + its status so
              the admin can reactivate an old service rather than create a duplicate. */}
          {nameDup && (
            <div className="col-span-full bg-[#FFF8E1] border border-[#FFE082] text-[#7A5C00] rounded-[8px] py-2 px-3 text-[12px]">
              ⚠️ A {nameDup.deactivated ? "DEACTIVATED" : "n active"} service named{" "}
              <strong>{nameDup.shelter_name}</strong> already exists
              {nameDup.shelter_code ? ` (${nameDup.shelter_code})` : ""}.{" "}
              {nameDup.deactivated
                ? "Consider reactivating it instead of creating a duplicate."
                : "Check this isn't a duplicate before continuing."}
            </div>
          )}
          <SelectField
            label="State"
            req
            value={newForm.state}
            onChange={onStateChange}
            options={STATES}
            placeholder="— Select —"
          />
          <SelectField
            label="DFV Service Type/Customer Type"
            req
            value={newForm.customer_type}
            onChange={setF("customer_type")}
            options={customerTypes || []}
            placeholder="— Select —"
          />
          <SelectField
            label="Sales Order Group"
            req
            value={newForm.sales_group}
            onChange={setF("sales_group")}
            options={salesGroups || []}
            placeholder="— Select —"
          />
          <SelectField
            label="Default Warehouse"
            req
            value={newForm.default_warehouse}
            onChange={setF("default_warehouse")}
            options={warehouses || []}
            placeholder="— Select —"
          />
          <SelectField
            label="Taxable"
            req
            value={newForm.taxable !== false ? "true" : "false"}
            onChange={function (v) {
              set({ taxable: v === "true" });
            }}
            options={[
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
            ]}
          />
          <SelectField
            label="Stock Held"
            value={newForm.stock_held === true ? "true" : "false"}
            onChange={function (v) {
              set({ stock_held: v === "true" });
            }}
            options={[
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
            ]}
          />
          <Field
            label="Date Onboarded"
            req
            type="date"
            value={newForm.date_onboarded}
            onChange={setF("date_onboarded")}
          />
          {/* Contact-model Phase 3 (Stage 1 de-wire): the shelter's Phone/Email are
              NOT captured here — they derive read-only from the Primary contact (the
              first caseworker, added on the Users step). The old shelters.phone/email
              columns drop in Stage 2. */}
          <Field
            label="ABN"
            value={newForm.abn}
            onChange={setF("abn")}
            placeholder="e.g. 12 345 678 901"
          />
          <div className="col-span-full">
            <label className={labelCls}>Internal Notes</label>
            <textarea
              value={newForm.internal_notes || ""}
              onChange={function (e) {
                set({ internal_notes: e.target.value });
              }}
              placeholder="Internal notes not visible to DFV Service users..."
              rows={2}
              className={`${inputCls} resize-y`}
              style={{ fontFamily: "inherit" }}
            />
          </div>
        </div>
      )}

      {/* STEP 2 — Address (physical + postal) */}
      {step === 1 && (
        <div>
          <div className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.5px] mb-2">
            Physical Address
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="col-span-full">
              <label className={labelCls}>Street Address</label>
              <AddressAutocomplete
                mapsReadyTick={mapsReadyTick}
                value={newForm.physical_line1 || ""}
                onChange={function (val) {
                  set({ physical_line1: val });
                }}
                onSelect={function (parts) {
                  set({
                    physical_line1: parts.street,
                    physical_line2: parts.street2 || newForm.physical_line2,
                    physical_suburb_city: parts.suburb || newForm.physical_suburb_city,
                    physical_state: parts.state || newForm.physical_state,
                    physical_postcode: parts.postcode || newForm.physical_postcode,
                  });
                }}
                placeholder="Start typing the physical address..."
                inputStyle={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: "1px solid #CFE0DE",
                  fontSize: 13,
                  color: "#1A2E2B",
                  fontFamily: "inherit",
                  background: "#FFFFFF",
                }}
              />
            </div>
            <Field
              label="Street Address 2"
              value={newForm.physical_line2}
              onChange={setF("physical_line2")}
            />
            <Field
              label="Suburb/City"
              value={newForm.physical_suburb_city}
              onChange={setF("physical_suburb_city")}
            />
            <SelectField
              label="State"
              value={newForm.physical_state}
              onChange={setF("physical_state")}
              options={STATES}
              placeholder="— Select —"
            />
            <Field
              label="Postcode"
              value={newForm.physical_postcode}
              onChange={setF("physical_postcode")}
            />
          </div>

          <div className="flex items-center justify-between mb-2">
            <div className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.5px]">
              Postal Address
            </div>
            <button
              type="button"
              onClick={copyPhysicalToPostal}
              className="text-[12px] font-semibold text-zephyr-teal-dark bg-transparent border-0 cursor-pointer p-0 underline"
            >
              Same as physical
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* Postal = mailing address: often a PO Box, which Google address
                autocomplete does NOT return, so this is a plain input (no autofill). */}
            <Field
              label="Street Address / PO Box"
              value={newForm.postal_line1}
              onChange={setF("postal_line1")}
              placeholder="e.g. PO Box 123"
              full
            />
            <Field
              label="Street Address 2"
              value={newForm.postal_line2}
              onChange={setF("postal_line2")}
            />
            <Field
              label="Suburb/City"
              value={newForm.postal_suburb_city}
              onChange={setF("postal_suburb_city")}
            />
            <SelectField
              label="State"
              value={newForm.postal_state}
              onChange={setF("postal_state")}
              options={STATES}
              placeholder="— Select —"
            />
            <Field
              label="Postcode"
              value={newForm.postal_postcode}
              onChange={setF("postal_postcode")}
            />
          </div>
        </div>
      )}

      {/* STEP 3 — Users / first caseworker (optional) */}
      {step === 2 && (
        <div>
          <div className="text-[13px] text-zephyr-muted mb-3">
            Optionally add the first caseworker. Leave blank to add users later from the service's
            Users tab.{" "}
            {cwNoLogin
              ? "They will be added as a contact only, with no sign-in — the service's first contact."
              : "If you enter an email + password, the user is created together with the service in one step."}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="First Name"
              value={newForm.cw_first_name}
              onChange={setF("cw_first_name")}
            />
            <Field label="Last Name" value={newForm.cw_last_name} onChange={setF("cw_last_name")} />
            <Field
              label={cwNoLogin ? "Email" : "Email (login)"}
              value={newForm.cw_email}
              onChange={setF("cw_email")}
              placeholder="user@example.com"
            />
            {/* Contact-only (No Login): no auth account is created, so the password
                field is hidden. The person can be given a login later from their
                edit form (Users tab → No login untick). */}
            {!cwNoLogin && (
              <div>
                <label className={labelCls}>Password</label>
                <input
                  type="password"
                  value={newForm.cw_password || ""}
                  onChange={function (e) {
                    set({ cw_password: e.target.value });
                  }}
                  placeholder="Min 12 chars"
                  className={inputCls}
                  style={{ fontFamily: "inherit" }}
                />
                <div className="text-[11px] text-zephyr-muted mt-[3px]">
                  Min 12 chars · upper + lower + number + symbol
                </div>
              </div>
            )}
            <Field
              label="Mobile"
              value={newForm.cw_mobile || ""}
              onChange={setF("cw_mobile")}
              placeholder="e.g. 0412 345 678"
            />
            <Field
              label="Phone"
              value={newForm.cw_phone || ""}
              onChange={setF("cw_phone")}
              placeholder="e.g. 07 3000 0000"
            />
            <SelectField
              label="Primary Contact Role"
              value={newForm.cw_primary_contact_label || "DFV Service Manager"}
              onChange={setF("cw_primary_contact_label")}
              options={CONTACT_ROLES}
            />
            <div className="col-span-full">
              <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={cwNoLogin}
                  onChange={function (e) {
                    set({ cw_no_login: e.target.checked });
                  }}
                />
                <span>No login account (contact only)</span>
              </label>
              <div className="text-[11px] text-zephyr-muted mt-1 ml-6">
                Tick to add this person as a contact on the new service with no sign-in. Enter at
                least a name. You can give them a login later from their edit form.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* STEP 4 — Default Delivery Address */}
      {step === 3 && (
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-full flex justify-end">
            <button
              type="button"
              onClick={copyPhysicalToDelivery}
              className="bg-transparent border-0 p-0 text-[12px] text-zephyr-teal cursor-pointer"
              style={{ fontFamily: "inherit" }}
            >
              ⧉ Copy from physical address
            </button>
          </div>
          {/* Address Name — required with a delivery street (it prints on the delivery;
              no more auto "Default Delivery"). Rule + wording: services/addressName.ts. */}
          <div className="col-span-full">
            <label htmlFor="wiz-addr-name" className={labelCls}>
              Address Name
              <ReqStar />
            </label>
            <input
              id="wiz-addr-name"
              type="text"
              value={newForm.delivery_address_name || ""}
              onChange={function (e) {
                setF("delivery_address_name")(e.target.value);
              }}
              aria-describedby="wiz-addr-name-help"
              placeholder={ADDRESS_NAME_PLACEHOLDER}
              className={inputCls}
              style={{ fontFamily: "inherit" }}
            />
            <AddressNameHelp id="wiz-addr-name-help" />
          </div>
          <div>
            <label className={labelCls}>Street Address</label>
            <AddressAutocomplete
              mapsReadyTick={mapsReadyTick}
              value={newForm.delivery_line1 || ""}
              onChange={function (val) {
                set({ delivery_line1: val });
              }}
              onSelect={function (parts) {
                set({
                  delivery_line1: parts.street,
                  delivery_line2: parts.street2 || newForm.delivery_line2,
                  delivery_suburb: parts.suburb || newForm.delivery_suburb,
                  delivery_state: parts.state || newForm.delivery_state,
                  delivery_postcode: parts.postcode || newForm.delivery_postcode,
                });
              }}
              placeholder="Start typing delivery address..."
              inputStyle={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #CFE0DE",
                fontSize: 13,
                color: "#1A2E2B",
                fontFamily: "inherit",
                background: "#FFFFFF",
              }}
            />
          </div>
          <Field
            label="Street Address 2"
            value={newForm.delivery_line2}
            onChange={setF("delivery_line2")}
          />
          <Field
            label="Suburb/City"
            value={newForm.delivery_suburb}
            onChange={setF("delivery_suburb")}
          />
          <Field label="State" value={newForm.delivery_state} onChange={setF("delivery_state")} />
          <Field
            label="Postcode"
            value={newForm.delivery_postcode}
            onChange={setF("delivery_postcode")}
          />
          <Field
            label="Delivery Instructions"
            value={newForm.delivery_instructions}
            onChange={setF("delivery_instructions")}
            placeholder="e.g. Buzz intercom 2, leave at front desk"
            full
          />
        </div>
      )}

      {newErr && (
        <div className="bg-[#FFEBEE] text-[#C62828] rounded-[8px] py-[10px] px-[14px] text-[13px] mt-3">
          {newErr}
        </div>
      )}

      {/* Footer nav */}
      <div className="flex gap-[10px] justify-between mt-5">
        <div>
          {step > 0 && (
            <Btn variant="secondary" onClick={back}>
              ← Back
            </Btn>
          )}
        </div>
        <div className="flex gap-[10px]">
          <Btn
            variant="secondary"
            onClick={function () {
              onClose();
              setNewErr("");
            }}
          >
            Cancel
          </Btn>
          {isLast ? (
            <Btn
              onClick={function () {
                createUser("shelters");
              }}
              disabled={newSaving}
            >
              {newSaving ? "Creating..." : "Create DFV Service"}
            </Btn>
          ) : (
            <Btn onClick={next}>Next →</Btn>
          )}
        </div>
      </div>
    </ModalBackdrop>
  );
}
